import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Paper,
  Typography,
  Button,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox
} from '@material-ui/core'
import {
  ExpandMore,
  ExpandLess,
  Check as ConfirmIcon,
  Close as RejectIcon,
  CompareArrows
} from '@material-ui/icons'

const scoreColor = (score) => {
  if (score >= 0.7) return '#4caf50'
  if (score >= 0.5) return '#ff9800'
  return '#9e9e9e'
}

const sourceTypeBadge = (sourceType) => {
  const config = {
    vendor_quote: { label: 'VQ', bg: '#ff9800' },
    plans: { label: 'PLAN', bg: '#4caf50' },
    bid_response: { label: 'BID', bg: '#1976d2' }
  }
  const c = config[sourceType] || config.bid_response
  return (
    <Chip
      label={c.label}
      size="small"
      style={{ backgroundColor: c.bg, color: '#fff', height: 20, fontSize: 10, fontWeight: 600, marginRight: 6 }}
    />
  )
}

const parseVqId = (id) => {
  if (!id || typeof id !== 'string') return null
  const match = id.match(/^vq_([a-f0-9]{24})_(\d+)$/)
  if (!match) return null
  return { quoteId: match[1], lineIndex: parseInt(match[2], 10) }
}

const MatchSuggestionsPanel = ({ projectId, url, onMatchConfirmed }) => {
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [collapsed, setCollapsed] = useState(false)
  const [confirming, setConfirming] = useState(new Set())
  const [expandedRows, setExpandedRows] = useState(new Set())
  // Track selected suggestions per source item: { brLineItemId: Set<suggestionIndex> }
  const [selected, setSelected] = useState({})

  useEffect(() => {
    if (!projectId || !url) return
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/line-items/match-suggestions`)
      .then(res => {
        setMatches(res.data.matches || [])
        setLoading(false)
      })
      .catch(() => {
        setMatches([])
        setLoading(false)
      })
  }, [projectId, url])

  const toggleSelect = (brId, suggIdx) => {
    setSelected(prev => {
      const current = new Set(prev[brId] || [])
      if (current.has(suggIdx)) current.delete(suggIdx)
      else current.add(suggIdx)
      return { ...prev, [brId]: current }
    })
  }

  const selectAll = (brId, count) => {
    setSelected(prev => {
      const current = new Set(prev[brId] || [])
      const allSelected = current.size === count
      if (allSelected) {
        return { ...prev, [brId]: new Set() }
      } else {
        return { ...prev, [brId]: new Set(Array.from({ length: count }, (_, i) => i)) }
      }
    })
  }

  const handleConfirmSelected = async (match) => {
    const brId = match.bidResponseLineItemId
    const sel = selected[brId]
    if (!sel || sel.size === 0) return

    setConfirming(prev => new Set([...prev, brId]))
    try {
      const planIds = []
      const vqMatches = []

      for (const idx of sel) {
        const sugg = match.suggestions[idx]
        if (!sugg) continue
        if (sugg.sourceType === 'vendor_quote') {
          const vqInfo = parseVqId(sugg.planLineItemId)
          if (vqInfo) vqMatches.push(vqInfo)
        } else {
          planIds.push(sugg.planLineItemId)
        }
      }

      const payload = { action: 'confirm' }
      if (planIds.length > 0) payload.planLineItemIds = planIds
      if (vqMatches.length > 0) payload.vendorQuoteMatches = vqMatches

      await axios.post(`${url}/api/bid-estimation/line-items/${brId}/confirm-match`, payload)
      setMatches(prev => prev.filter(m => m.bidResponseLineItemId !== brId))
      setSelected(prev => { const n = { ...prev }; delete n[brId]; return n })
      if (onMatchConfirmed) onMatchConfirmed()
    } catch (err) {
      console.error('Confirm match failed:', err)
    } finally {
      setConfirming(prev => {
        const next = new Set(prev)
        next.delete(brId)
        return next
      })
    }
  }

  const handleReject = async (brLineItemId) => {
    setConfirming(prev => new Set([...prev, brLineItemId]))
    try {
      await axios.post(`${url}/api/bid-estimation/line-items/${brLineItemId}/confirm-match`, {
        action: 'reject'
      })
      setMatches(prev => prev.filter(m => m.bidResponseLineItemId !== brLineItemId))
    } catch (err) {
      console.error('Reject match failed:', err)
    } finally {
      setConfirming(prev => {
        const next = new Set(prev)
        next.delete(brLineItemId)
        return next
      })
    }
  }

  const toggleExpand = (brLineItemId) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(brLineItemId)) next.delete(brLineItemId)
      else next.add(brLineItemId)
      return next
    })
  }

  if (!loading && matches.length === 0) return null

  return (
    <Paper style={{ padding: 16, marginBottom: 16, border: '1px solid #bbdefb', backgroundColor: '#e3f2fd' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CompareArrows style={{ color: '#1565c0' }} />
          <Typography variant="subtitle2" style={{ color: '#1565c0' }}>
            Match Suggestions
          </Typography>
          <Chip label={loading ? '...' : matches.length} size="small"
            style={{ backgroundColor: '#1565c0', color: '#fff', height: 22 }} />
        </div>
        <IconButton size="small" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </div>

      <Collapse in={!collapsed}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
              Items that may match across sources. Check the correct matches and confirm, or reject all.
            </Typography>
            <Table size="small" style={{ backgroundColor: '#fff', borderRadius: 4 }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 40 }} />
                  <TableCell>Source Item</TableCell>
                  <TableCell>Match Candidate</TableCell>
                  <TableCell align="center">Score</TableCell>
                  <TableCell align="center">Reasons</TableCell>
                  <TableCell align="center" style={{ width: 160 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matches.map(match => {
                  const best = match.suggestions[0]
                  if (!best) return null
                  const brId = match.bidResponseLineItemId
                  const isProcessing = confirming.has(brId)
                  const isExpanded = expandedRows.has(brId)
                  const alternates = match.suggestions.slice(1)
                  const sel = selected[brId] || new Set()
                  const allSuggestions = match.suggestions

                  return (
                    <React.Fragment key={brId}>
                      {/* Best match row */}
                      <TableRow hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={sel.has(0)}
                            onChange={() => toggleSelect(brId, 0)}
                          />
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            {sourceTypeBadge(match.sourceType || 'bid_response')}
                            <div>
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {match.description}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {match.quantity} {match.unit}
                              </Typography>
                              {match.notes && (
                                <Typography variant="caption" style={{ display: 'block', color: '#7b1fa2', fontSize: 10, marginTop: 2 }}>
                                  {match.notes}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            {sourceTypeBadge(best.sourceType || 'plans')}
                            <div>
                              <Typography variant="body2">
                                {best.description}
                                {best.unitPrice > 0 && (
                                  <span style={{ marginLeft: 8, color: '#2e7d32', fontWeight: 600 }}>
                                    ${best.unitPrice.toFixed(2)}
                                  </span>
                                )}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {best.quantity} {best.unit}
                                {best.vendor && (
                                  <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{best.vendor}</span>
                                )}
                                {alternates.length > 0 && (
                                  <span
                                    style={{ marginLeft: 8, color: '#1565c0', cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => toggleExpand(brId)}
                                  >
                                    {isExpanded ? 'collapse' : `+${alternates.length} more`}
                                  </span>
                                )}
                              </Typography>
                              {best.notes && (
                                <Typography variant="caption" style={{ display: 'block', color: '#7b1fa2', fontSize: 10, marginTop: 2 }}>
                                  {best.notes}
                                </Typography>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${Math.round(best.score * 100)}%`}
                            size="small"
                            style={{ backgroundColor: scoreColor(best.score), color: '#fff', fontWeight: 600, height: 22 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {best.reasons.filter(r => r !== 'generic_penalty').map(r => (
                            <Chip key={r} label={r} size="small"
                              variant="outlined" style={{ margin: 1, height: 20, fontSize: 11 }} />
                          ))}
                        </TableCell>
                        <TableCell align="center">
                          {isProcessing ? (
                            <CircularProgress size={20} />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={sel.size === 0}
                                onClick={() => handleConfirmSelected(match)}
                                style={{
                                  backgroundColor: sel.size > 0 ? '#4caf50' : '#e0e0e0',
                                  color: '#fff',
                                  textTransform: 'none',
                                  fontSize: 11,
                                  minWidth: 0,
                                  padding: '2px 8px'
                                }}
                              >
                                Confirm{sel.size > 0 ? ` (${sel.size})` : ''}
                              </Button>
                              <IconButton
                                size="small"
                                title="Reject all suggestions for this item"
                                style={{ color: '#f44336' }}
                                onClick={() => handleReject(brId)}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded alternate rows with checkboxes */}
                      {isExpanded && alternates.map((alt, idx) => {
                        const suggIdx = idx + 1
                        return (
                          <TableRow key={`${brId}_alt_${idx}`} style={{ backgroundColor: '#fafafa' }}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={sel.has(suggIdx)}
                                onChange={() => toggleSelect(brId, suggIdx)}
                              />
                            </TableCell>
                            <TableCell />
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'flex-start', paddingLeft: 16 }}>
                                {sourceTypeBadge(alt.sourceType || 'plans')}
                                <div>
                                  <Typography variant="body2" style={{ fontSize: 13 }}>
                                    {alt.description}
                                    {alt.unitPrice > 0 && (
                                      <span style={{ marginLeft: 8, color: '#2e7d32', fontWeight: 600 }}>
                                        ${alt.unitPrice.toFixed(2)}
                                      </span>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {alt.quantity} {alt.unit}
                                    {alt.vendor && (
                                      <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{alt.vendor}</span>
                                    )}
                                  </Typography>
                                  {alt.notes && (
                                    <Typography variant="caption" style={{ display: 'block', color: '#7b1fa2', fontSize: 10, marginTop: 2 }}>
                                      {alt.notes}
                                    </Typography>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${Math.round(alt.score * 100)}%`}
                                size="small"
                                style={{ backgroundColor: scoreColor(alt.score), color: '#fff', fontWeight: 600, height: 22 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {alt.reasons.filter(r => r !== 'generic_penalty').map(r => (
                                <Chip key={r} label={r} size="small"
                                  variant="outlined" style={{ margin: 1, height: 20, fontSize: 11 }} />
                              ))}
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Collapse>
    </Paper>
  )
}

export default MatchSuggestionsPanel
